import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CustomInput,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
} from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { CardTitle, Button, Field, FormAlerts } from '@cordata/buffet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { components } from 'react-select';
import { format } from 'date-fns';

import useSlice from '~/utils/slice/hook';
import SliceFields from '~/components/Slice/Fields';
import useApi from '~/utils/api/hook';

import {
  referenceAdminInstance as refAdmin,
  toastSuccess,
  toastError,
} from '~/utils';

const SurveyRouteSync = ({
  selectedSurvey,
  setFieldValue,
  uiRouteOptions,
  setUiRouteOptions,
  saveUiRoute,
}) => {
  useEffect(() => {
    if (selectedSurvey && selectedSurvey.value) {
      const existingRoute = uiRouteOptions.find(r => r.value === 'patient.surveys');
      const routeOption = existingRoute || { label: 'patient.surveys', value: 'patient.surveys' };
      if (!existingRoute) {
        setUiRouteOptions(prev => [...prev, routeOption]);
        saveUiRoute('patient.surveys');
      }
      setFieldValue('route', routeOption);
      setFieldValue('queryParams', JSON.stringify({ surveyType: selectedSurvey.value }));
    }
  }, [selectedSurvey]);

  return null;
};

SurveyRouteSync.propTypes = {
  selectedSurvey: PropTypes.shape(),
  setFieldValue: PropTypes.func.isRequired,
  uiRouteOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setUiRouteOptions: PropTypes.func.isRequired,
  saveUiRoute: PropTypes.func.isRequired,
};

SurveyRouteSync.defaultProps = {
  selectedSurvey: null,
};

const ReportRouteSync = ({
  selectedReport,
  setFieldValue,
  uiRouteOptions,
  setUiRouteOptions,
  saveUiRoute,
}) => {
  useEffect(() => {
    if (selectedReport && selectedReport.value) {
      const routeValue = `reports.configured:${selectedReport.value}`;
      const existingRoute = uiRouteOptions.find(r => r.value === routeValue);
      const routeOption = existingRoute || { label: routeValue, value: routeValue };
      if (!existingRoute) {
        setUiRouteOptions(prev => [...prev, routeOption]);
        saveUiRoute(routeValue);
      }
      setFieldValue('route', routeOption);
      setFieldValue('queryParams', '');
    }
  }, [selectedReport]);

  return null;
};

ReportRouteSync.propTypes = {
  selectedReport: PropTypes.shape(),
  setFieldValue: PropTypes.func.isRequired,
  uiRouteOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setUiRouteOptions: PropTypes.func.isRequired,
  saveUiRoute: PropTypes.func.isRequired,
};

ReportRouteSync.defaultProps = {
  selectedReport: null,
};

const MultiValueContainer = ({
  data,
  selectProps,
  ...props
}) => {
  const toggleItem = (e) => {
    e.stopPropagation();

    if (selectProps.onToggleFeatureFlag) {
      selectProps.onToggleFeatureFlag(data.value);
    }
  };

  return (
    <div className="d-flex">
      <span
        role="button"
        tabIndex={0}
        onMouseDown={toggleItem}
        className={`rounded p-1 text-center font-weight-bold text-light ${data.needs ? 'bg-success' : 'bg-danger'}`}
        style={{
          width: 42,
          height: 26,
          marginTop: 2,
          fontSize: '0.8rem',
          cursor: 'pointer',
        }}
        title={`In order for the user to see this item, the ${data.label} feature flag must ${!data.needs ? 'NOT ' : ''}be set.`}
      >
        {data.needs ? 'HAS' : 'NOT'}
      </span>

      <components.MultiValueContainer {...props} />
    </div>
  );
};

MultiValueContainer.propTypes = {
  data: PropTypes.shape().isRequired,
  selectProps: PropTypes.shape().isRequired,
};

const SLICE_TYPE_NORMALIZE = {
  org: 'organization',
  organization: 'organization',
  division: 'division',
  market: 'market',
  specialty: 'specialty',
  'sub-specialty': 'sub-specialty',
  system: 'system',
};

const getItemSliceType = (attrs) => {
  if (attrs.divisionUid) return 'division';
  if (attrs.orgUid) return 'organization';
  if (attrs.subSpecialtyUid) return 'sub-specialty';
  if (attrs.specialtyUid) return 'specialty';
  if (attrs.marketUid) return 'market';
  return 'system';
};

const SLICE_LABEL_MAP = {
  system: 'System',
  market: 'Market',
  specialty: 'Specialty',
  'sub-specialty': 'Sub-Specialty',
  organization: 'Organization',
  division: 'Division',
};

const SideNavConfigForm = ({
  data,
  items,
  reloadItems,
  confirmDelete,
  cancelEdit,
  currentModule,
  licenseTierOptions,
  pageSlice,
  onCreateOverride,
}) => {
  const { api } = useApi();
  const {
    currentSlice,
    serialize: serializeSlice,
    markets,
    specialties,
    subSpecialties,
    organizations,
    divisions,
  } = useSlice();

  const [uiRouteOptions, setUiRouteOptions] = useState([]);
  const [featureFlagOptions, setFeatureFlagOptions] = useState([]);
  const [surveyOptions, setSurveyOptions] = useState([]);
  const [reportConfigOptions, setReportConfigOptions] = useState([]);
  const [modifiedUser, setModifiedUser] = useState('unknown');
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [includeChildren, setIncludeChildren] = useState(false);

  useEffect(() => {
    setModifiedUser('unknown');
    if (api && (data.attributes.updatedUid || data.attributes.createdUid)) {
      const u = data.attributes.updatedUid || data.attributes.createdUid;

      api
        .get(`app-user/${u}`)
        .then(r => r.data)
        .then((r) => {
          if (r) {
            setModifiedUser(r.data.attributes.userName);
          }
        })
        .catch(() => {});
    }
  }, [api, data.attributes.updatedUid, data.attributes.createdUid]);

  const fetchUIRoutes = () =>
    refAdmin
      .get('/reference-ui-route?$slice_type=system&page=1&page-size=1000')
      .then(r => r.data.data)
      .then(r => r.map(rr => ({
        label: `${rr.attributes.route}${rr.attributes.eventTypeName ? ` (${rr.attributes.eventTypeName})` : ''}`,
        value: rr.attributes.route,
      })))
      .then(setUiRouteOptions)
      .catch(() => toastError('Error loading UI routes'));

  const fetchFeatureFlags = () =>
    refAdmin
      .get(`/reference-feature-flag/preview?$slice_type=${currentSlice.type}&$slice_id=${currentSlice && currentSlice.id}&page=1&page-size=1000`)
      .then(r => r.data.data)
      .then(r => r.map(rr => ({
        label: rr.attributes.featureFlagLabel,
        value: rr.attributes.featureFlagName,
        needs: true,
      })))
      .then(setFeatureFlagOptions)
      .catch(() => toastError('Error loading feature flags'));

  const saveUiRoute = route =>
    refAdmin
      .post('/reference-ui-route?$slice_type=system', {
        route,
      })
      .then(fetchUIRoutes);

  const fetchSurveys = () => {
    if (currentModule !== 'patient') return Promise.resolve();

    return refAdmin
      .get('/reference-survey?$slice_type=system&page=1&page-size=1000')
      .then(r => r.data.data)
      .then(r => r
        .map(s => ({
          label: s.attributes.surveyTitle,
          value: s.attributes.surveyTitle,
        }))
        .filter((s, i, arr) => arr.findIndex(x => x.value === s.value) === i)
        .sort((a, b) => a.label.localeCompare(b.label)))
      .then(setSurveyOptions)
      .catch(() => setSurveyOptions([]));
  };

  const fetchReportConfigs = () => {
    if (currentModule !== 'reports') return Promise.resolve();

    return refAdmin
      .get(`/reference-report-configuration/preview?$slice_type=${currentSlice.type}&$slice_id=${currentSlice && currentSlice.id}&page=1&page-size=1000`)
      .then(r => r.data.data)
      .then(r => r
        .filter(rc => rc.attributes.isActive)
        .map(rc => ({
          label: rc.attributes.reportConfigurationLabel,
          value: rc.attributes.reportConfigurationName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)))
      .then(setReportConfigOptions)
      .catch(() => setReportConfigOptions([]));
  };

  useEffect(() => {
    setOptionsLoaded(false);
    Promise.all([fetchUIRoutes(), fetchFeatureFlags(), fetchSurveys(), fetchReportConfigs()])
      .finally(() => setOptionsLoaded(true));
  }, [currentModule, currentSlice]);

  if (!optionsLoaded) {
    return (
      <Card className="sticky-top" style={{ top: 20 }}>
        <CardBody className="text-center p-4 text-muted">Loading...</CardBody>
      </Card>
    );
  }

  const pageSliceType = SLICE_TYPE_NORMALIZE[pageSlice && pageSlice.type] || 'system';
  const itemSliceType = getItemSliceType(data.attributes);
  const isInherited = data.id && pageSliceType !== 'system' && itemSliceType !== pageSliceType;
  const hasChildren = data.attributes.navItemName &&
    items.some(i => i.attributes.parentNavItemName === data.attributes.navItemName);
  const childItems = hasChildren
    ? items.filter(i => i.attributes.parentNavItemName === data.attributes.navItemName)
    : [];

  const buildOverrideAttrs = (sourceItem) => {
    const attrs = { ...sourceItem.attributes };

    delete attrs.createdDate;
    delete attrs.updatedDate;
    delete attrs.createdUid;
    delete attrs.updatedUid;

    attrs.marketUid = pageSlice.type === 'market' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.market) || null;
    attrs.specialtyUid = pageSlice.type === 'specialty' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.specialty) || null;
    attrs.subSpecialtyUid = pageSlice.type === 'sub-specialty' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.subSpecialty) || null;
    attrs.orgUid = pageSlice.type === 'organization' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.organization) || null;
    attrs.divisionUid = pageSlice.type === 'division' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.division) || null;

    return attrs;
  };

  const targetMarketUid = pageSlice && (pageSlice.type === 'market' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.market) || null);
  const targetSpecialtyUid = pageSlice && (pageSlice.type === 'specialty' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.specialty) || null);
  const targetSubSpecialtyUid = pageSlice && (pageSlice.type === 'sub-specialty' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.subSpecialty) || null);
  const targetOrgUid = pageSlice && (pageSlice.type === 'organization' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.organization) || null);
  const targetDivisionUid = pageSlice && (pageSlice.type === 'division' ? pageSlice.id : (pageSlice.lineage && pageSlice.lineage.division) || null);

  const sliceLookup = (list, uid) => {
    const found = (list || []).find(s => s.id === uid);
    return found ? found.label : uid;
  };

  const targetMarketLabel = targetMarketUid && sliceLookup(markets, targetMarketUid);
  const targetSpecialtyLabel = targetSpecialtyUid && sliceLookup(specialties, targetSpecialtyUid);
  const targetSubSpecialtyLabel = targetSubSpecialtyUid
    && sliceLookup(subSpecialties, targetSubSpecialtyUid);
  const targetOrgLabel = targetOrgUid && sliceLookup(organizations, targetOrgUid);
  const targetDivisionLabel = targetDivisionUid && sliceLookup(divisions, targetDivisionUid);

  const existsAtTargetContext = (sourceItem) =>
    items.some(existing =>
      existing.attributes.navItemName === sourceItem.attributes.navItemName &&
      (existing.attributes.marketUid || null) === targetMarketUid &&
      (existing.attributes.specialtyUid || null) === targetSpecialtyUid &&
      (existing.attributes.subSpecialtyUid || null) === targetSubSpecialtyUid &&
      (existing.attributes.orgUid || null) === targetOrgUid &&
      (existing.attributes.divisionUid || null) === targetDivisionUid);

  const parentAlreadyExists = existsAtTargetContext(data);
  const newChildren = childItems.filter(c => !existsAtTargetContext(c));
  const skippedChildren = childItems.filter(c => existsAtTargetContext(c));

  const handleConfirmOverride = () => {
    const itemsToCreate = [];

    if (!parentAlreadyExists) {
      itemsToCreate.push({ attributes: buildOverrideAttrs(data) });
    }

    if (includeChildren) {
      newChildren.forEach(child => {
        itemsToCreate.push({ attributes: buildOverrideAttrs(child) });
      });
    }

    setShowOverrideModal(false);
    setIncludeChildren(false);
    onCreateOverride(itemsToCreate);
  };

  const isSurveyRoute = data.attributes &&
    data.attributes.route === 'patient.surveys' &&
    data.attributes.queryParams &&
    data.attributes.queryParams.includes('surveyType');

  const isConfiguredReportRoute = data.attributes &&
    data.attributes.route &&
    data.attributes.route.startsWith('reports.configured:');

  const parseSurveyTitle = () => {
    try {
      const parsed = JSON.parse(data.attributes.queryParams);
      return parsed.surveyType || null;
    } catch {
      return null;
    }
  };

  const parseReportConfigName = () => {
    const parts = data.attributes.route.split(':');
    return parts.length > 1 ? parts[1] : null;
  };

  const mergedFeatureFlagOptions = [...featureFlagOptions];
  if (data.attributes.featureFlag) {
    data.attributes.featureFlag.forEach((existing) => {
      const value = existing.startsWith('!') ? existing.slice(1) : existing;
      if (!mergedFeatureFlagOptions.some(ff => ff.value === value)) {
        mergedFeatureFlagOptions.push({ label: value, value, needs: !existing.startsWith('!') });
      }
    });
  }

  /* eslint-disable react/jsx-closing-tag-location */
  return (<>
    <Formik
      initialValues={{
        ...data.attributes,
        id: data.id,
        route: (data.attributes &&
                data.attributes.route &&
                (uiRouteOptions.find(uiRoute => uiRoute.value === data.attributes.route) || {
                  label: data.attributes.route,
                  value: data.attributes.route,
                })),
        currentWhen: (data.attributes &&
                      data.attributes.currentWhen &&
                      data.attributes.currentWhen.map(route =>
                        uiRouteOptions.find(uiRoute => uiRoute.value === route) || {
                          label: route,
                          value: route,
                        })),
        featureFlag: (data.attributes &&
                      data.attributes.featureFlag &&
                      data.attributes.featureFlag.map((existing) => {
                        const value = existing.startsWith('!') ? existing.slice(1) : existing;
                        const needs = !existing.startsWith('!');

                        const found = featureFlagOptions.find(ff => value === ff.value);

                        return found
                          ? { ...found, needs }
                          : { label: value, value, needs };
                      })) || [],
        licenseTierLevel: (data.attributes &&
                           data.attributes.licenseTierLevel &&
                           licenseTierOptions.find(lt =>
                             lt.value === data.attributes.licenseTierLevel)) ||
                           licenseTierOptions.find(lt => lt.value === 0), // default to Starter
        currentSlice: (data.attributes.divisionUid && 'division') ||
                      (data.attributes.orgUid && 'organization') ||
                      (data.attributes.subSpecialtyUid && 'sub-specialty') ||
                      (data.attributes.specialtyUid && 'specialty') ||
                      (data.attributes.marketUid && 'market') ||
                      'system',

        // can't edit the rest of these directly from the form, but need them
        // here so they get saved when adding new items from the preview
        parentNavItemName: (data.attributes && data.attributes.parentNavItemName) || null,
        defaultSortOrder: (data.attributes && data.attributes.defaultSortOrder) || 0,

        isSurvey: !!isSurveyRoute,
        selectedSurvey: isSurveyRoute
          ? surveyOptions.find(s => s.value === parseSurveyTitle()) || null
          : null,
        isConfiguredReport: !!isConfiguredReportRoute,
        selectedReport: isConfiguredReportRoute
          ? reportConfigOptions.find(r => r.value === parseReportConfigName()) || null
          : null,
      }}

      validationSchema={Yup.object().shape({
        navItemName: Yup.string().required(),
        navItemLabel: Yup.string().required(),
        route: Yup.string().nullable(),
        currentWhen: Yup.string().nullable(),
        queryParams: Yup.string().nullable(),
        href: Yup.string().nullable(),
        openInNewTab: Yup.boolean(),
        featureFlag: Yup.array(Yup.string()).nullable(),
        licenseTierLevel: Yup.object().shape({
          label: Yup.string(),
          value: Yup.number().integer().nullable(),
        }).nullable(),
      })}

      onSubmit={async (values, { setErrors, setSubmitting }) => {
        const method = values.id ? 'put' : 'post';
        const url = values.id ? `/reference-nav-item/${values.id}` : '/reference-nav-item';

        const appModule = currentModule;
        const featureFlag = values.featureFlag && values.featureFlag.map(f => (f.needs ? f.value : `!${f.value}`));
        const licenseTierLevel = values.licenseTierLevel && values.licenseTierLevel.value;
        const route = values.route && values.route.value;
        const currentWhen = values.currentWhen && values.currentWhen.map(r => r.value);

        if (values.queryParams) {
          try {
            JSON.parse(values.queryParams);
          } catch {
            setErrors({ queryParams: 'Invalid JSON' });
            setSubmitting(false);
            return;
          }
        }

        const sliceStuff = serializeSlice(values);

        if (!values.id && values.navItemName) {
          try {
            const existing = await refAdmin
              .get(`/reference-nav-item/preview?ref-data-type=${appModule}&$slice_type=${sliceStuff.$slice_type}&$slice_id=${sliceStuff.$slice_id}&page=1&page-size=1000`)
              .then(r => r.data.data);
            if (existing.some(i => i.attributes.navItemName === values.navItemName)) {
              setErrors({ navItemName: 'An item with this name already exists at the selected context level' });
              setSubmitting(false);
              return;
            }
          } catch {
            // If the check fails, proceed with save and let the server enforce the constraint
          }
        }

        const p = {
          ...values,
          appModule,
          featureFlag,
          licenseTierLevel,
          route,
          currentWhen,
          ...sliceStuff,
        };

        delete p.isSurvey;
        delete p.selectedSurvey;
        delete p.isConfiguredReport;
        delete p.selectedReport;

        refAdmin[method](url, p)
          .then(() => toastSuccess())
          .then(reloadItems)
          .then(() => cancelEdit())
          .catch((err) => {
            const serverMessage = err
              && err.response
              && err.response.data
              && err.response.data.message;
            if (serverMessage) {
              toastError(serverMessage);
            } else {
              setErrors(err);
            }
          })
          .finally(() => setSubmitting(false));
      }}

      displayName="SideNavConfigForm"
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {({
        values,
        errors,
        handleSubmit,
        isSubmitting,
        submitCount,
        setFieldValue,
      }) => (
        <Card className="sticky-top" style={{ top: 20 }}>
          <form onSubmit={handleSubmit} noValidate>
            <CardBody style={{ maxHeight: '80vh', overflowY: 'scroll' }} className="p-0">
              { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

              {isInherited && (
                <Alert color="info" className="m-2 d-flex align-items-center justify-content-between">
                  <span>
                    This item is inherited from <strong>{SLICE_LABEL_MAP[itemSliceType]}</strong>.
                  </span>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setShowOverrideModal(true)}
                  >
                    Create Override at {SLICE_LABEL_MAP[pageSliceType]}
                  </Button>
                </Alert>
              )}

              {currentModule === 'patient' && values.isSurvey && (
                <SurveyRouteSync
                  selectedSurvey={values.selectedSurvey}
                  setFieldValue={setFieldValue}
                  uiRouteOptions={uiRouteOptions}
                  setUiRouteOptions={setUiRouteOptions}
                  saveUiRoute={saveUiRoute}
                />
              )}

              {currentModule === 'reports' && values.isConfiguredReport && (
                <ReportRouteSync
                  selectedReport={values.selectedReport}
                  setFieldValue={setFieldValue}
                  uiRouteOptions={uiRouteOptions}
                  setUiRouteOptions={setUiRouteOptions}
                  saveUiRoute={saveUiRoute}
                />
              )}

              <fieldset>
                <Row form>
                  <Col>
                    <Field
                      name="navItemName"
                      required
                      disabled={!!data.id}
                      text="Name is the programmatic handle. It cannot be changed after creation — create a new item instead."
                    />
                  </Col>

                  <Col>
                    <Field
                      name="navItemLabel"
                      required
                      text="The text that will be displayed on the item in the UI."
                    />
                  </Col>
                </Row>

                {currentModule === 'patient' && (
                  <Field
                    name="isSurvey"
                    label="Is This a Survey?"
                    type="checkbox"
                    onChange={() => {
                      const next = !values.isSurvey;
                      setFieldValue('isSurvey', next);
                      if (!next) {
                        setFieldValue('selectedSurvey', null);
                        setFieldValue('route', null);
                        setFieldValue('queryParams', '');
                      }
                    }}
                  />
                )}

                {currentModule === 'patient' && values.isSurvey && (
                  <Field
                    name="selectedSurvey"
                    label="Survey"
                    type="select"
                    options={surveyOptions}
                    placeholder="Select a survey..."
                  />
                )}

                {currentModule === 'reports' && (
                  <Field
                    name="isConfiguredReport"
                    label="Is This a No-Code Report?"
                    type="checkbox"
                    onChange={() => {
                      const next = !values.isConfiguredReport;
                      setFieldValue('isConfiguredReport', next);
                      if (!next) {
                        setFieldValue('selectedReport', null);
                        setFieldValue('route', null);
                        setFieldValue('queryParams', '');
                      }
                    }}
                  />
                )}

                {currentModule === 'reports' && values.isConfiguredReport && (
                  <Field
                    name="selectedReport"
                    label="Report Configuration"
                    type="select"
                    options={reportConfigOptions}
                    placeholder="Select a report configuration..."
                  />
                )}

                {!values.isSurvey && !values.isConfiguredReport && (
                <Row form>
                  <Col>
                    <Field
                      name="route"
                      type="select"
                      options={uiRouteOptions}
                      creatable
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          label: inputValue,
                          value: inputValue,
                        };

                        setUiRouteOptions([...uiRouteOptions, newOption]);

                        saveUiRoute(inputValue);

                        setFieldValue('route', newOption);
                      }}
                      onChange={(id, v) => setFieldValue(id, { label: v, value: v })}
                      placeholder="Select or start typing to add new options"
                      text="A route is where the nav item should take the user and is of the form 'patient.demographics' or 'reports.qrt.interactions'. Press Enter to confirm a new route."
                    />
                  </Col>

                  <Col>
                    <Field
                      name="currentWhen"
                      type="select"
                      multi
                      options={uiRouteOptions}
                      creatable
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          label: inputValue,
                          value: inputValue,
                        };

                        setUiRouteOptions([...uiRouteOptions, newOption]);

                        saveUiRoute(inputValue);

                        setFieldValue('currentWhen', [...values.currentWhen, newOption]);
                      }}
                      placeholder="Select or start typing to add new options"
                      text="'Current when' is a list of routes for which the nav item will appear to be active when the user is on any of those routes. Press Enter to confirm a new value."
                    />
                  </Col>
                </Row>
                )}

                {!values.isSurvey && !values.isConfiguredReport && (
                <Field
                  name="queryParams"
                  text="JSON defining the query params for the route. Usually only applies for surveys. Contact a dev for more info."
                />
                )}

                <Row form>
                  <Col>
                    <Field
                      name="href"
                      type="url"
                      placeholder="i.e. https://google.com"
                      text="Used for links to external sites or whenever you need to use a full url instead of a route."
                    />
                  </Col>

                  <Col>
                    <Field
                      name="openInNewTab"
                      placeholder="Yes"
                      type="checkbox"
                      text="If checked, clicking on this item will cause a new browser tab to be opened."
                    />
                  </Col>
                </Row>

                <Field
                  name="featureFlag"
                  type="select"
                  multi
                  creatable
                  onCreateOption={(inputValue) => {
                  const newOption = {
                    label: inputValue,
                    value: inputValue,
                    needs: true,
                  };

                  setFeatureFlagOptions([...featureFlagOptions, newOption]);

                  setFieldValue('featureFlag', [...values.featureFlag, newOption]);
                }}
                  closeMenuOnSelect={false}
                  options={mergedFeatureFlagOptions}
                  components={{ MultiValueContainer }}
                  onToggleFeatureFlag={(flagValue) => {
                    const updated = values.featureFlag.map(f =>
                      (f.value === flagValue ? { ...f, needs: !f.needs } : f));
                    setFieldValue('featureFlag', updated);
                  }}
                  placeholder="Select or start typing to add new options"
                  text="A list of feature flags that will be used to determine whether or not to show the item. Uses AND logic (all conditions specified must be true for the item to show)."
                />

                <Field
                  name="licenseTierLevel"
                  type="select"
                  options={licenseTierOptions}
                  text="Minimum license tier required for this item to be visible. If Starter, this item will be visible to everyone."
                />
              </fieldset>

              <fieldset>
                <Field
                  name="isActive"
                  type="checkbox"
                />
              </fieldset>

              <fieldset>
                <CardTitle>Context</CardTitle>

                <SliceFields />
              </fieldset>
            </CardBody>

            <CardFooter>
              <div className="d-flex align-items-center">
                <Button outline onClick={() => confirmDelete(data)} color="danger" className="mr-auto">
                  <FontAwesomeIcon icon="trash" />
                  <span className="d-none d-md-inline ml-2">Delete</span>
                </Button>

                <Button outline color="tertiary" type="cancel" onClick={cancelEdit} className="mr-2">
                  Cancel
                </Button>

                <Button type="submit">
                  Sav{isSubmitting ? 'ing ...' : 'e'}
                </Button>
              </div>

              {(data.attributes.updatedDate || data.attributes.createdDate) && (
                <div className="text-muted text-small mt-2">
                  Last Updated: {format(data.attributes.updatedDate || data.attributes.createdDate, 'ddd MMM Do YYYY [@] h:mm A')} by {modifiedUser}
                </div>
              )}
            </CardFooter>
          </form>
        </Card>
      )}
    </Formik>

    <Modal isOpen={showOverrideModal} toggle={() => setShowOverrideModal(false)}>
      <ModalHeader toggle={() => setShowOverrideModal(false)}>Create Override</ModalHeader>

      <ModalBody>
        <p>
          Create an override at
          the <strong>{SLICE_LABEL_MAP[pageSliceType]}</strong> level.
        </p>

        <p className="text-muted small mb-1">Context fields that will be set:</p>
        <ul className="text-muted small">
          {targetMarketLabel && (
          <li>Market: {targetMarketLabel}</li>
            )}
          {targetSpecialtyLabel && (
          <li>Specialty: {targetSpecialtyLabel}</li>
            )}
          {targetSubSpecialtyLabel && (
          <li>Sub-Specialty: {targetSubSpecialtyLabel}</li>
            )}
          {targetOrgLabel && (
          <li>Organization: {targetOrgLabel}</li>
            )}
          {targetDivisionLabel && (
          <li>Division: {targetDivisionLabel}</li>
            )}
        </ul>

        {hasChildren && (
        <CustomInput
          type="checkbox"
          id="includeChildren"
          label="Include child items"
          checked={includeChildren}
          onChange={e => setIncludeChildren(e.target.checked)}
          className="mb-3"
        />
          )}

        <p className="small mb-1"><strong>Items to create:</strong></p>
        <ul className="small mb-0 list-unstyled">
          {parentAlreadyExists ? (
            <li className="text-muted">{data.attributes.navItemLabel} (already exists, will be skipped)</li>
            ) : (
              <li className="text-success">{data.attributes.navItemLabel}</li>
            )}

          {includeChildren && newChildren.map(child => (
            <li key={child.id} className="text-success" style={{ marginLeft: '1.5rem' }}>{child.attributes.navItemLabel}</li>
            ))}

          {includeChildren && skippedChildren.map(child => (
            <li key={child.id} className="text-muted" style={{ marginLeft: '1.5rem' }}>{child.attributes.navItemLabel} (already exists, will be skipped)</li>
            ))}
        </ul>
      </ModalBody>

      <ModalFooter>
        <Button outline color="tertiary" onClick={() => { setShowOverrideModal(false); setIncludeChildren(false); }}>
          Cancel
        </Button>

        <Button
          color="primary"
          onClick={handleConfirmOverride}
          disabled={parentAlreadyExists && (!includeChildren || newChildren.length === 0)}
        >
          Create Override
        </Button>
      </ModalFooter>
    </Modal>
  </>);
  /* eslint-enable react/jsx-closing-tag-location */
};

SideNavConfigForm.propTypes = {
  data: PropTypes.shape().isRequired,
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  reloadItems: PropTypes.func.isRequired,
  confirmDelete: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  currentModule: PropTypes.string.isRequired,
  licenseTierOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  pageSlice: PropTypes.shape(),
  onCreateOverride: PropTypes.func,
};

SideNavConfigForm.defaultProps = {
  pageSlice: null,
  onCreateOverride: () => {},
};

export default SideNavConfigForm;
